import { FetchHelper } from '@/globals/FetchHelper';
import { ChatMessage, ChatMessages, MessageType } from './types';
import { ChatParameters, ChatStartParameters, ChatMessageParameters, ChatUpdateParameters, ChatLastMessageParameters } from './pn-chat.models';

export class PnChatService {
  private readonly _endpoint;
  private readonly _market;
  private readonly _language;
  private readonly _currentUrl;
  private fetchHelper = new FetchHelper('PnChatService');
  private readonly _startChatPath = `/api/sfchat/start-chat`;
  private readonly _fetchChatMessagesPath = `/api/sfchat/get-conversation-entries`;
  private readonly _sendTextPath = `/api/sfchat/send-text-message`;
  private readonly _sendChoicePath = `/api/sfchat/send-choice-message`;
  private readonly _updateLastMessagePath = `/api/sfchat/update-last-message-id`;
  private readonly _hasNewMessagesPath = `/api/sfchat/has-new-messages`;

  constructor(endpoint: string, market: string, language: string) {
    this._endpoint = this.formatEndpoint(endpoint);
    this._market = market;
    this._language = language;
    this._currentUrl = window.location.href;
  }

  async startChat(chatId: string, itemId: string): Promise<string> {
    let model: ChatStartParameters = 
    {
      market: this._market,
      language: this._language,
      chatIdString: chatId,
      itemId: itemId,
      currentUrl: this._currentUrl
    };

    const fetchUrl = this._endpoint + this._startChatPath;
    const data = (await this.fetchHelper.fetchJson(
      fetchUrl,
      this.getRequestInit(model),
      false
    )) as string;

    return this.handleResponse<string>(data, chatId);
  }

  async fetchMessages(chatId: string, prevMessageData: ChatMessages) : Promise<ChatMessages> {
    if (!chatId) {
      return prevMessageData;
    }

    let model: ChatParameters = {
      market: this._market,
      language: this._language,
      chatIdString: chatId
    };

    const fetchUrl = this._endpoint + this._fetchChatMessagesPath;
    const data = (await this.fetchHelper.fetchJson(
      fetchUrl,
      this.getRequestInit(model),
      false,
    )) as string;

    if (!data) {
      return prevMessageData;
    }
    
    return this.handleResponse<ChatMessages>(data, prevMessageData);
  }

  async sendMessage(content : string, itemId : string, chatId: string, lastMessageId: string, messageType: MessageType) : Promise<string> {
    let newLatestMessageId;

    switch (messageType) {
      case 'Choice':
        newLatestMessageId = await this.sendChoiceMessage(content, itemId, chatId, lastMessageId);
        break;
      default:
        newLatestMessageId = await this.sendTextMessage(content, itemId, chatId, lastMessageId);
        break;
    }

    if (!newLatestMessageId) {
      return lastMessageId;
    }

    if (newLatestMessageId.includes(",")) {
      newLatestMessageId = newLatestMessageId.substring(newLatestMessageId.lastIndexOf(",") + 1);
    }

    return newLatestMessageId;
  }

  async updateLastMessageId(chatId: string, lastMessageId: string) {
    let model: ChatLastMessageParameters = {
      chatIdString: chatId,
      latestMessageIdString: lastMessageId
    };

    const fetchUrl = this._endpoint + this._updateLastMessagePath;
    await this.fetchHelper.fetchJson(
      fetchUrl,
      this.getRequestInit(model),
      false,
    );
  }

  async hasNewMessages(itemId : string) : Promise<boolean> {
    let model: ChatStartParameters = {
      language: this._language,
      market: this._market,
      itemId: itemId,
      chatIdString: "",
      currentUrl: this._currentUrl
    };

    const fetchUrl = this._endpoint + this._hasNewMessagesPath;
    let data = await this.fetchHelper.fetchJson(
      fetchUrl,
      this.getRequestInit(model),
      false,
    ) as string;

    if (!data) {
      return false;
    }

    return JSON.parse(data) as boolean;
  }

  private async sendTextMessage(content : string, itemId : string, chatId: string, defaultReturn: string) : Promise<string> {
    if (!chatId || !content) {
      return defaultReturn;
    }

    let model: ChatMessageParameters = {
      market: this._market,
      language: this._language,
      chatIdString: chatId,
      content: content,
      itemId: itemId,
      currentUrl: this._currentUrl
    };

    const fetchUrl = this._endpoint + this._sendTextPath;
    const data = (await this.fetchHelper.fetchJson(
      fetchUrl,
      this.getRequestInit(model),
      false,
    )) as string;

    return this.handleResponse<string>(data, defaultReturn)?.id ?? defaultReturn;
  }

  private async sendChoiceMessage(content : string, itemId : string, chatId: string, defaultReturn: string) : Promise<string> {
    if (!chatId || !content) {
      return defaultReturn;
    }

    let model: ChatMessageParameters = 
    {
      market: this._market,
      language: this._language,
      chatIdString: chatId,
      content: content,
      itemId: itemId,
      currentUrl: this._currentUrl
    };

    const fetchUrl = this._endpoint + this._sendChoicePath;
    const data = (await this.fetchHelper.fetchJson(
      fetchUrl,
      this.getRequestInit(model),
      false,
    )) as string;

    return this.handleResponse<string>(data, defaultReturn)?.id ?? defaultReturn;
  }

  private formatEndpoint(endpoint: string) : string {
    return endpoint.lastIndexOf('/') === endpoint.length - 1 ? endpoint.substring(0, endpoint.length - 1) : endpoint;
  }

  private handleResponse<T>(data : any, defaultReturn : any, checkForArray: boolean = false) {
    if (!data) {
      return defaultReturn;
    }

    let parsedData = JSON.parse(data) as T;
    if (!parsedData || checkForArray && !Array.isArray(parsedData)) {
      return defaultReturn;
    }

    return parsedData;
  }
  
  private getRequestInit(model: any, method: string = 'POST', mode: RequestMode = 'cors', contentTypes: string = 'application/json'): RequestInit {
    return {
      mode: mode,
      method: method,
      headers: 
      {
        'Content-Type': contentTypes,
        'CustomValidateAntiForgeryToken': "01001101010001010100111101010111"
      },
      body: JSON.stringify(model)
    };
  }

  GetLastMessageId(messages : ChatMessage[], lastMessageId : string) {
    return messages?.at(-1)?.messageId ?? lastMessageId;
  }
}
